import { bookingFailureKind } from "../booking/attempt.js";
import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useState } from "react";
import { markCreditedLessonUnpaid } from "../booking/creditPaymentReversal.js";
import { staffBooking } from "../booking/staff.js";

export function UndoPaymentRecord({
  appointmentId,
  paymentId,
  studentId,
  creditFunded,
  idempotencyKey,
  onBusyChange,
  onChanged,
  lessonDescription,
}: {
  lessonDescription?: string;
  appointmentId: number;
  paymentId: number | null;
  studentId: string;
  creditFunded: boolean;
  /**
   * Owned by the drawer, not this panel: backing out and reopening 'Mark as
   * unpaid' unmounts and remounts this component, and a retry after an
   * ambiguous failure has to carry the same key or it posts a second reversal.
   */
  idempotencyKey: string;
  onBusyChange?: (busy: boolean) => void;
  onChanged: () => void;
}) {
  const publish = usePublishActionOutcome();
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  function setReversalBusy(nextBusy: boolean) {
    setBusy(nextBusy);
    onBusyChange?.(nextBusy);
  }

  async function confirm() {
    if (busy) return;
    setReversalBusy(true);
    setProblem(null);
    try {
      if (creditFunded) {
        await markCreditedLessonUnpaid(appointmentId, studentId, idempotencyKey);
      } else {
        if (paymentId === null) {
          throw new Error("The payment record could not be identified safely.");
        }
        await staffBooking.markPaymentPending(
          appointmentId,
          paymentId,
          { portal_student_id: studentId },
          idempotencyKey,
        );
      }
      let balance: number | null = null;
      if (creditFunded) {
        try {
          const refreshed = await staffBooking.lessonCredits(studentId);
          if (refreshed.portal_student_id === studentId && Number.isFinite(refreshed.total_available_credits)) balance = refreshed.total_available_credits;
        } catch { /* The reversal was confirmed; a failed read must not invite another write. */ }
      }
      const result: ActionOutcome = { variant: creditFunded && balance === null ? "attention" : "success", title: "Payment record corrected",
        description: lessonDescription ?? `Lesson appointment #${appointmentId}`,
        details: [{ value: "The lesson remains booked and is marked unpaid. No money was refunded or charged." },
          ...(creditFunded ? [{ value: "The lesson credit allocation was reversed." }, ...(balance !== null ? [{ label: "Remaining balance", value: `${balance} credits` }] : [{ value: "The current balance could not be refreshed. Check credits before making another correction." }])] : [{ value: "No lesson credits changed." }])] };
      if (!publish(result)) setOutcome(result);
      onChanged();
    } catch (cause: unknown) {
      setUncertain(bookingFailureKind(cause) === "uncertain");
      setProblem(
        cause instanceof Error
          ? cause.message
          : creditFunded
            ? "The lesson credit could not be restored."
            : "The payment record could not be corrected.",
      );
    } finally {
      setReversalBusy(false);
    }
  }

  if (outcome) return <ActionConfirmation {...outcome} reveal />;

  return (
    <div className="change-lesson cal-drawer-operation">
      <h3>{creditFunded ? "Mark as unpaid and restore credit?" : "Mark as unpaid?"}</h3>
      {creditFunded ? (
        <>
          <p>
            This lesson was settled with a lesson credit. Marking it unpaid will return 1 credit to the student's balance.
          </p>
          <p className="small muted">
            The lesson stays booked. Only the credit allocation is reversed; no money is refunded or charged.
          </p>
        </>
      ) : (
        <>
          <p>
            Use this only to correct a lesson that was marked paid by mistake.
          </p>
          <p className="small muted">
            This changes the payment record back to unpaid. It does not refund any money and it does not add, remove, or return lesson credits.
          </p>
        </>
      )}
      {problem && (uncertain ? <ActionConfirmation variant="uncertain" title="Payment correction needs checking" description={problem} details={[{ value: "Check the authoritative payment and credit state before retrying. Retry this same correction rather than starting another." }]} /> : <div className="error small" role="alert">{problem}</div>)}
      <button type="button" disabled={busy} onClick={() => void confirm()}>
        {busy
          ? "Marking as unpaid…"
          : creditFunded
            ? "Mark as unpaid and restore credit"
            : "Confirm mark as unpaid"}
      </button>
    </div>
  );
}
